export const VX_CACHE_PREFIX: string = 'VX_CACHE-';
export const defaultCacheTimeInSeconds: number = 29;
export const logSourceChartsCacheSeconds: number = 7 * 60;
export const chronicleChartsCacheSeconds: number = 25 * 60;

// --- Configuration ---

/**
 * Maximum number of entries the cache will hold.
 * When exceeded, the least-recently-used entries are evicted.
 */
const MAX_CACHE_ENTRIES = 500;

/**
 * Maximum total byte size the cache is allowed to consume (approximately).
 * 20 MB — covers heavy dashboard usage without risking tab OOM.
 */
const MAX_CACHE_BYTES = 20 * 1024 * 1024;

/**
 * Maximum byte size for a single cache entry.
 * Entries exceeding this are not cached — they're too large to justify
 * evicting many smaller entries.
 */
const MAX_ENTRY_BYTES = 2 * 1024 * 1024; // 2 MB

/**
 * Threshold (80%) at which a console warning is logged.
 */
const CACHE_BYTES_WARNING_THRESHOLD = MAX_CACHE_BYTES * 0.8;

/**
 * Interval (ms) for the background sweep that removes expired entries.
 */
const CLEANUP_INTERVAL_MS = 60_000;

/**
 * How long (ms) a failed fetch is "negatively cached" to avoid
 * hammering a failing endpoint on every render cycle.
 */
const NEGATIVE_CACHE_TTL_MS = 5_000;

// --- Doubly-linked list node for O(1) LRU ---

interface LRUNode {
  key: string;
  prev: LRUNode | null;
  next: LRUNode | null;
}

interface CacheEntry<T = unknown> {
  data: T;
  expiresAt: number;
  writeSeq: number;
  byteSize: number;
  node: LRUNode;
}

// Sentinel head/tail for the doubly-linked list.
// Most-recently-used is at tail.prev, least-recently-used is at head.next.
const head: LRUNode = { key: '__HEAD__', prev: null, next: null };
const tail: LRUNode = { key: '__TAIL__', prev: null, next: null };
head.next = tail;
tail.prev = head;

function removeNode(node: LRUNode): void {
  const prev = node.prev!;
  const next = node.next!;
  prev.next = next;
  next.prev = prev;
  node.prev = null;
  node.next = null;
}

function insertBeforeTail(node: LRUNode): void {
  const prev = tail.prev!;
  prev.next = node;
  node.prev = prev;
  node.next = tail;
  tail.prev = node;
}

function moveToTail(node: LRUNode): void {
  removeNode(node);
  insertBeforeTail(node);
}

// --- State ---

let writeSequence = 0;
let totalBytes = 0;
let hasWarnedMemoryPressure = false;

const cache = new Map<string, CacheEntry>();

/**
 * Map of in-flight fetch promises for request deduplication.
 */
const inFlightFetches = new Map<string, Promise<unknown>>();

/**
 * Set of keys that recently failed fetching (negative cache).
 * Prevents repeated requests to a failing endpoint within NEGATIVE_CACHE_TTL_MS.
 */
const negativeCache = new Map<string, number>(); // key → expiresAt timestamp

// --- Background cleanup (lazy start/stop) ---

let cleanupTimer: ReturnType<typeof setInterval> | null = null;

function startCleanupTimer(): void {
  if (cleanupTimer !== null) return;
  cleanupTimer = setInterval(() => {
    const now = Date.now();
    for (const [key, entry] of cache) {
      if (entry.expiresAt <= now) {
        removeCacheEntry(key, entry);
      }
    }
    // Clean expired negative cache entries
    for (const [key, expiresAt] of negativeCache) {
      if (expiresAt <= now) {
        negativeCache.delete(key);
      }
    }
    // Reset warning flag if we've dropped back below threshold
    if (totalBytes < CACHE_BYTES_WARNING_THRESHOLD) {
      hasWarnedMemoryPressure = false;
    }
    // Stop timer if cache is empty — no work to do
    if (cache.size === 0 && negativeCache.size === 0) {
      stopCleanupTimer();
    }
  }, CLEANUP_INTERVAL_MS);
}

function stopCleanupTimer(): void {
  if (cleanupTimer !== null) {
    clearInterval(cleanupTimer);
    cleanupTimer = null;
  }
}

// --- Internal helpers ---

function removeCacheEntry(key: string, entry: CacheEntry): void {
  totalBytes -= entry.byteSize;
  removeNode(entry.node);
  cache.delete(key);
}

/**
 * Evicts the least-recently-used entries (from head.next forward)
 * until both entry count and byte budget are satisfied.
 * O(k) where k = number of entries evicted (typically 1).
 */
function evictIfNeeded(): void {
  while (cache.size > MAX_CACHE_ENTRIES || totalBytes > MAX_CACHE_BYTES) {
    const lruNode = head.next!;
    if (lruNode === tail) break; // empty list, shouldn't happen
    const entry = cache.get(lruNode.key);
    if (!entry) break;
    removeCacheEntry(lruNode.key, entry);
  }
}

/**
 * Estimates the byte size of data using a shallow heuristic.
 * Much cheaper than JSON.stringify for large payloads.
 *
 * - Strings: length × 2 (UTF-16)
 * - Numbers/booleans: 8 bytes
 * - Arrays: sum of elements (sampled for large arrays)
 * - Objects: sum of key + value sizes (sampled for large objects)
 * - null/undefined: 0
 */
function estimateByteSize(value: unknown, depth = 0): number {
  if (value === null || value === undefined) return 0;
  if (depth > 5) return 64; // avoid deep recursion, rough estimate

  switch (typeof value) {
    case 'string':
      return (value as string).length * 2;
    case 'number':
    case 'boolean':
      return 8;
    case 'object': {
      if (Array.isArray(value)) {
        const arr = value as unknown[];
        if (arr.length === 0) return 16;
        if (arr.length <= 20) {
          // Small arrays: measure all elements
          let size = 16; // array overhead
          for (const item of arr) {
            size += estimateByteSize(item, depth + 1);
          }
          return size;
        }
        // Large arrays: sample first 10 elements and extrapolate
        let sampleSize = 0;
        const sampleCount = Math.min(10, arr.length);
        for (let i = 0; i < sampleCount; i++) {
          sampleSize += estimateByteSize(arr[i], depth + 1);
        }
        return 16 + (sampleSize / sampleCount) * arr.length;
      }

      // Plain object
      const obj = value as Record<string, unknown>;
      const keys = Object.keys(obj);
      if (keys.length === 0) return 16;
      if (keys.length <= 20) {
        let size = 16;
        for (const k of keys) {
          size += k.length * 2 + estimateByteSize(obj[k], depth + 1);
        }
        return size;
      }
      // Large objects: sample first 10 keys and extrapolate
      let sampleSize = 0;
      const sampleCount = Math.min(10, keys.length);
      for (let i = 0; i < sampleCount; i++) {
        sampleSize += keys[i].length * 2 + estimateByteSize(obj[keys[i]], depth + 1);
      }
      return 16 + (sampleSize / sampleCount) * keys.length;
    }
    default:
      return 64;
  }
}

/**
 * Validates that a cache key uses the VX_CACHE_PREFIX namespace.
 * If not, prepends it automatically to prevent cross-feature collisions.
 */
function normalizeKey(key: string): string {
  if (key.startsWith(VX_CACHE_PREFIX)) return key;
  return VX_CACHE_PREFIX + key;
}

// --- Public API ---

export function getCacheEntry<T = unknown>(key: string): T | null {
  const nKey = normalizeKey(key);
  const entry = cache.get(nKey);
  if (!entry) return null;

  if (entry.expiresAt <= Date.now()) {
    removeCacheEntry(nKey, entry);
    return null;
  }

  // O(1) LRU promotion
  moveToTail(entry.node);

  return entry.data as T;
}

/**
 * @param key - Cache key (auto-prefixed with VX_CACHE_PREFIX if not already)
 * @param data - Data to cache
 * @param ttl - Time-to-live in seconds, or false/0 to skip caching
 * @param sequenceNumber - Optional write sequence number for race protection
 */
export function setCacheEntry<T = unknown>(
  key: string,
  data: T,
  ttl: number | boolean,
  sequenceNumber?: number,
): void {
  if (!ttl || typeof ttl === 'boolean') return;

  const nKey = normalizeKey(key);
  const seq = sequenceNumber ?? ++writeSequence;

  // Race condition guard: don't overwrite with newer data
  const existing = cache.get(nKey);
  if (existing && existing.writeSeq > seq) {
    return;
  }

  // Estimate byte size using cheap heuristic
  const byteSize = estimateByteSize(data);

  // Per-entry size guard: don't cache oversized payloads
  if (byteSize > MAX_ENTRY_BYTES) {
    if (import.meta.env?.DEV) {
      console.debug(
        `[inMemoryCache] Skipping oversized entry "${nKey}" ` +
          `(${(byteSize / 1024 / 1024).toFixed(2)} MB > ${MAX_ENTRY_BYTES / 1024 / 1024} MB limit)`,
      );
    }
    return;
  }

  // Remove old entry's byte contribution before overwriting
  if (existing) {
    totalBytes -= existing.byteSize;
    removeNode(existing.node);
  }

  totalBytes += byteSize;

  const node: LRUNode = { key: nKey, prev: null, next: null };
  insertBeforeTail(node);

  cache.set(nKey, {
    data,
    expiresAt: Date.now() + ttl * 1000,
    writeSeq: seq,
    byteSize,
    node,
  });

  // Warn once when approaching the memory ceiling
  if (!hasWarnedMemoryPressure && totalBytes >= CACHE_BYTES_WARNING_THRESHOLD) {
    hasWarnedMemoryPressure = true;
    console.warn(
      `[inMemoryCache] Memory pressure: cache is at ${(totalBytes / 1024 / 1024).toFixed(1)} MB ` +
        `(${Math.round((totalBytes / MAX_CACHE_BYTES) * 100)}% of ${MAX_CACHE_BYTES / 1024 / 1024} MB limit). ` +
        `LRU eviction will engage shortly.`,
    );
  }

  evictIfNeeded();

  // Start cleanup timer lazily on first write
  startCleanupTimer();
}

/**
 * Returns a monotonically increasing write sequence number.
 * Use this to stamp concurrent requests so that only the latest write wins.
 */
export function nextWriteSequence(): number {
  return ++writeSequence;
}

/**
 * Fetch-through helper with built-in request deduplication and retry awareness.
 * - If cached, returns immediately.
 * - If in-flight, deduplicates (returns same promise).
 * - If recently failed (negative cache), rejects immediately without hitting the network.
 * - On failure, applies a short negative-cache TTL to prevent thundering herd.
 *
 * @param key - Cache key
 * @param fetcher - Async function that produces the data
 * @param ttl - Time-to-live in seconds for the cached result
 * @returns The cached or freshly-fetched data
 */
export async function getOrFetch<T = unknown>(
  key: string,
  fetcher: () => Promise<T>,
  ttl: number,
): Promise<T> {
  const nKey = normalizeKey(key);

  // Return cached value if available
  const cached = getCacheEntry<T>(key);
  if (cached !== null) return cached;

  // Check negative cache — avoid hammering a failing endpoint
  const negExpiry = negativeCache.get(nKey);
  if (negExpiry && negExpiry > Date.now()) {
    return Promise.reject(
      new Error(
        `[inMemoryCache] "${nKey}" is in negative cache (failed recently). Retry after ${Math.ceil((negExpiry - Date.now()) / 1000)}s.`,
      ),
    );
  }

  // Deduplicate in-flight requests
  const existing = inFlightFetches.get(nKey);
  if (existing) return existing as Promise<T>;

  const seq = nextWriteSequence();

  const promise = fetcher()
    .then((data) => {
      setCacheEntry(key, data, ttl, seq);
      // Clear negative cache on success
      negativeCache.delete(nKey);
      return data;
    })
    .catch((err: unknown) => {
      // Apply negative cache to avoid retrying immediately
      negativeCache.set(nKey, Date.now() + NEGATIVE_CACHE_TTL_MS);
      startCleanupTimer(); // ensure timer runs to clean up negative cache
      throw err;
    })
    .finally(() => {
      inFlightFetches.delete(nKey);
    });

  inFlightFetches.set(nKey, promise);
  return promise;
}

export function deleteCacheEntry(key: string): void {
  const nKey = normalizeKey(key);
  const entry = cache.get(nKey);
  if (entry) {
    removeCacheEntry(nKey, entry);
  }
  inFlightFetches.delete(nKey);
  negativeCache.delete(nKey);
}

export function deleteCacheEntries(keys: string[]): void {
  for (const key of keys) {
    deleteCacheEntry(key);
  }
}

export function clearAllCache(): void {
  cache.clear();
  inFlightFetches.clear();
  negativeCache.clear();
  totalBytes = 0;
  hasWarnedMemoryPressure = false;
  // Reset linked list
  head.next = tail;
  tail.prev = head;
  stopCleanupTimer();
}

export function hasCacheEntry(key: string): boolean {
  return getCacheEntry(key) !== null;
}

/**
 * Returns diagnostic information about the cache state.
 */
export function getCacheStats(): {
  size: number;
  maxSize: number;
  totalBytes: number;
  maxBytes: number;
  maxEntryBytes: number;
  usagePercent: number;
  inFlight: number;
  negativeCacheSize: number;
} {
  return {
    size: cache.size,
    maxSize: MAX_CACHE_ENTRIES,
    totalBytes,
    maxBytes: MAX_CACHE_BYTES,
    maxEntryBytes: MAX_ENTRY_BYTES,
    usagePercent: Math.round((totalBytes / MAX_CACHE_BYTES) * 100),
    inFlight: inFlightFetches.size,
    negativeCacheSize: negativeCache.size,
  };
}

/**
 * Stops the background cleanup timer and resets all state.
 * Call during app teardown or in test environments.
 */
export function destroyCache(): void {
  stopCleanupTimer();
  cache.clear();
  inFlightFetches.clear();
  negativeCache.clear();
  writeSequence = 0;
  totalBytes = 0;
  hasWarnedMemoryPressure = false;
  head.next = tail;
  tail.prev = head;
}
